import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SessionInfo } from '@agentic-os/contracts';
import { api, ApiRequestError, setCsrfToken } from './api';

interface SessionState {
  session: SessionInfo | null;
  loading: boolean;
  can: (capability: string) => boolean;
}

const Ctx = createContext<SessionState>({ session: null, loading: true, can: () => false });

export function SessionProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api<SessionInfo>('/api/v1/session');
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    refetchInterval: 60_000,
  });
  const session = q.data ?? null;
  useEffect(() => setCsrfToken(session?.csrfToken ?? null), [session?.csrfToken]);
  return (
    <Ctx.Provider
      value={{
        session,
        loading: q.isPending,
        can: (c) => session?.capabilities.includes(c) ?? false,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useSession = () => useContext(Ctx);
